import { useQuery } from '@tanstack/react-query'

/**
 * The run "Let Graphit set this up" started on this page load (25i), shared
 * with the app shell's banner through the query cache: `/me` is read once
 * per load, so it can't know about a run started since. The plan and the
 * skips are kept in session storage too, so a reload can still name what
 * failed and retry it.
 */

const ACTIVE_RUN_QUERY_KEY = 'rock-setup-active-run'
const PLAN_STORAGE_PREFIX = 'graphit.rockSetup.autoPlan.'

/** @param {string|undefined} tenantId */
export const activeRunKey = (tenantId) => [ACTIVE_RUN_QUERY_KEY, tenantId]

/**
 * @typedef {Object} ActiveRunRecord
 * @property {string} connectionId
 * @property {string} runId
 * @property {Array<Object>} plan - the run's plan rows
 * @property {Array<string>} skipKeys - the person's skips, sent again on Retry
 */

/**
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string|undefined} tenantId
 * @param {ActiveRunRecord} record
 */
export function publishActiveRun(queryClient, tenantId, record) {
  queryClient.setQueryData(activeRunKey(tenantId), record)
  try {
    window.sessionStorage.setItem(PLAN_STORAGE_PREFIX + record.runId, JSON.stringify({ plan: record.plan, skipKeys: record.skipKeys }))
  } catch {
    // Storage blocked: the cache entry still serves this load.
  }
}

/**
 * @param {string} runId
 * @returns {{plan: Array<Object>, skipKeys: Array<string>}|null} what was stored for that run, when this tab started it
 */
export function recallRun(runId) {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(PLAN_STORAGE_PREFIX + runId) || 'null')
    if (!stored || !Array.isArray(stored.plan) || !Array.isArray(stored.skipKeys)) return null
    return stored
  } catch {
    return null
  }
}

/**
 * Subscribes to the published record (never fetches).
 *
 * @param {string|undefined} tenantId
 * @returns {ActiveRunRecord|undefined}
 */
export function useActiveRunRecord(tenantId) {
  return useQuery({
    queryKey: activeRunKey(tenantId),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  }).data
}
